import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { QueryClientProvider } from '@/components/QueryClientProvider';
import SessionProvider from '@/providers/SessionProvider';
import { StripeProvider } from '@/providers/StripeProvider';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { DashboardPage } from '@/pages/DashboardPage';
import { NewProduct } from '@/pages/NewProduct';
import { ProductDetail } from '@/pages/ProductDetail';
import LoginPage from '@/pages/auth/LoginPage';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage';
// Registration pages - commented out since registration is now invitation-only
// import RegisterPage from '@/pages/auth/RegisterPage';
// import OrganizationRegisterPage from '@/pages/auth/OrganizationRegisterPage';
import ProductsPage from '@/pages/ProductsPage';
import AttributesPage from '@/pages/AttributesPage';
import AttributeGroupsPage from '@/pages/AttributeGroupsPage';
import { ENABLE_CUSTOM_ATTRIBUTES, ENABLE_ATTRIBUTE_GROUPS } from '@/config/featureFlags';
import AdminOnly from '@/components/auth/AdminOnly';
import PermissionGuard from '@/components/auth/PermissionGuard';
import { FamilyFormPage } from '@/features/families/FamilyFormPage';
import { FamilyDetailPage } from '@/features/families/FamilyDetailPage';
import { SplashLoader } from '@/components/SplashLoader';
import { ScrollToTop } from '@/components/ScrollToTop';
// import { TourProvider } from '@/components/tours/TourProvider';

// Marketing Pages
import LandingPage from '@/pages/marketing/LandingPage';
import PricingPage from '@/pages/marketing/PricingPage';
import ProductPage from '@/pages/marketing/ProductPage';
import DemoPage from '@/pages/marketing/DemoPage';

// App Pages
import ReportsPage from './pages/ReportsPage';
import UploadPage from './pages/UploadPage';
import DocumentationPage from './pages/DocumentationPage';
import TeamPage from './pages/TeamPage';
import SettingsPage from './pages/SettingsPage';
import TeamHistoryPage from './pages/TeamHistoryPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import SetPasswordPage from './pages/auth/SetPasswordPage';
import CreateOrganizationPage from './pages/CreateOrganizationPage';
import RequestOrganizationPage from './pages/RequestOrganizationPage';
import { SearchResultsPage } from './pages/SearchResultsPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentCancelledPage from './pages/PaymentCancelledPage';

// Legal Pages
import TermsOfServicePage from './pages/legal/TermsOfServicePage';
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage';
import CookiePolicyPage from './pages/legal/CookiePolicyPage';

// Admin Pages
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { SystemHealth } from '@/pages/admin/SystemHealth';
import { UserManagement } from './pages/admin/UserManagement';
import { OrganizationAnalytics } from './pages/admin/OrganizationAnalytics';
import { OrganizationManagement } from './pages/admin/OrganizationManagement';
import { BackgroundJobs } from './pages/admin/BackgroundJobs';
import { AuditLogs } from './pages/admin/AuditLogs';

export const App: React.FC = () => {
  // Remove App.css styles that might be causing issues
  useEffect(() => {
    // Fix potential CSS issues with #root element
    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.style.maxWidth = 'none';
      rootElement.style.padding = '0';
      rootElement.style.margin = '0';
      rootElement.style.textAlign = 'left';
      rootElement.style.width = '100%';
      rootElement.style.height = '100vh';
      rootElement.style.overflow = 'auto';
      rootElement.style.scrollBehavior = 'auto'; // Ensure no smooth scrolling interferes
    }

    // Also ensure body and html elements don't interfere
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.documentElement.style.scrollBehavior = 'auto';
  }, []);

  // Protected route component that handles authentication
  const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
    const { isAuthenticated, loading } = useAuth();

    // While checking authentication status, show loading spinner
    if (loading) {
      return <SplashLoader />;
    }

    // If not authenticated, redirect to login
    if (!isAuthenticated) {
      return <Navigate to="/login" replace />;
    }

    // If authenticated, render the protected content
    return <>{children}</>;
  };

  return (
    <Router>
      <ThemeProvider defaultTheme="light" storageKey="ui-theme">
        <AuthProvider>
          <SessionProvider warningVariant="banner" enableStatePersistence={true}>
            <QueryClientProvider>
              <StripeProvider>
                <TooltipProvider>
                  <>
                    <Toaster
                      position="top-right"
                      richColors
                      duration={2000}
                      toastOptions={{
                        classNames: {
                          toast:
                            'border border-enterprise-200 bg-white text-enterprise-900 shadow-lg',
                          title: 'text-sm font-semibold',
                          description: 'text-sm text-enterprise-600',
                          actionButton: 'bg-primary-600 text-white hover:bg-primary-700',
                          cancelButton:
                            'bg-enterprise-100 text-enterprise-700 hover:bg-enterprise-200',
                        },
                      }}
                    />
                    <ScrollToTop />
                    <Routes>
                      {/* Marketing Routes */}
                      <Route path="/" element={<LandingPage />} />
                      <Route path="/marketing" element={<LandingPage />} />
                      <Route path="/marketing/pricing" element={<PricingPage />} />
                      <Route path="/marketing/product" element={<ProductPage />} />
                      <Route path="/demo" element={<DemoPage />} />
                      <Route path="/request-organization" element={<RequestOrganizationPage />} />

                      {/* Payment Routes */}
                      <Route path="/payment-success" element={<PaymentSuccessPage />} />
                      <Route path="/payment-cancelled" element={<PaymentCancelledPage />} />

                      {/* Auth Routes */}
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                      <Route path="/reset-password" element={<ResetPasswordPage />} />
                      {/* Registration now invitation-only - handled through invitation flow */}
                      {/* <Route path="/register" element={<RegisterPage />} />
                      <Route path="/register/:orgId" element={<OrganizationRegisterPage />} /> */}

                      {/* Legal Routes */}
                      <Route path="/terms" element={<TermsOfServicePage />} />
                      <Route path="/privacy" element={<PrivacyPolicyPage />} />
                      <Route path="/cookies" element={<CookiePolicyPage />} />

                      {/* Invitation Routes */}
                      <Route path="/invite/create-org" element={<CreateOrganizationPage />} />
                      <Route
                        path="/accept-invite/:membershipId/:token"
                        element={<AcceptInvitePage />}
                      />
                      <Route path="/set-password/:orgId" element={<SetPasswordPage />} />

                      {/* Protected App Routes */}
                      <Route
                        path="/app"
                        element={
                          <ProtectedRoute>
                            <DashboardLayout>
                              <DashboardPage />
                            </DashboardLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/app/products"
                        element={
                          <ProtectedRoute>
                            <DashboardLayout>
                              <ProductsPage />
                            </DashboardLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/app/products/new"
                        element={
                          <ProtectedRoute>
                            <DashboardLayout>
                              <PermissionGuard permission="product.add">
                                <NewProduct />
                              </PermissionGuard>
                            </DashboardLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/app/products/families"
                        element={<Navigate to="/app/settings?tab=families" replace />}
                      />
                      <Route
                        path="/app/products/families/new"
                        element={
                          <ProtectedRoute>
                            <DashboardLayout>
                              <PermissionGuard permission="product.add">
                                <FamilyFormPage mode="create" />
                              </PermissionGuard>
                            </DashboardLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/app/products/families/:id"
                        element={
                          <ProtectedRoute>
                            <DashboardLayout>
                              <PermissionGuard permission="product.view">
                                <FamilyDetailPage />
                              </PermissionGuard>
                            </DashboardLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/app/products/families/:id/edit"
                        element={
                          <ProtectedRoute>
                            <DashboardLayout>
                              <PermissionGuard permission="product.change">
                                <FamilyFormPage mode="edit" />
                              </PermissionGuard>
                            </DashboardLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/app/products/:id"
                        element={
                          <ProtectedRoute>
                            <DashboardLayout>
                              <ProductDetail />
                            </DashboardLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/app/reports"
                        element={
                          <ProtectedRoute>
                            <DashboardLayout>
                              <ReportsPage />
                            </DashboardLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/app/search"
                        element={
                          <ProtectedRoute>
                            <DashboardLayout>
                              <SearchResultsPage />
                            </DashboardLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/app/upload"
                        element={
                          <ProtectedRoute>
                            <DashboardLayout>
                              <UploadPage />
                            </DashboardLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/app/documentation"
                        element={
                          <ProtectedRoute>
                            <DashboardLayout>
                              <DocumentationPage />
                            </DashboardLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/app/team"
                        element={
                          <ProtectedRoute>
                            <DashboardLayout>
                              <TeamPage />
                            </DashboardLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/app/team/history"
                        element={
                          <ProtectedRoute>
                            <DashboardLayout>
                              <TeamHistoryPage />
                            </DashboardLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/app/settings"
                        element={
                          <ProtectedRoute>
                            <DashboardLayout>
                              <SettingsPage />
                            </DashboardLayout>
                          </ProtectedRoute>
                        }
                      />
                      {/* Attributes Page (with feature flag) */}
                      {ENABLE_CUSTOM_ATTRIBUTES && (
                        <Route
                          path="/app/attributes"
                          element={
                            <ProtectedRoute>
                              <DashboardLayout>
                                <AttributesPage />
                              </DashboardLayout>
                            </ProtectedRoute>
                          }
                        />
                      )}
                      {/* Attribute Groups Page (with feature flag) */}
                      {ENABLE_ATTRIBUTE_GROUPS && (
                        <Route
                          path="/app/settings/attribute-groups"
                          element={
                            <ProtectedRoute>
                              <DashboardLayout>
                                <AttributeGroupsPage />
                              </DashboardLayout>
                            </ProtectedRoute>
                          }
                        />
                      )}

                      {/* Admin Routes */}
                      <Route
                        path="/app/admin"
                        element={
                          <ProtectedRoute>
                            <AdminOnly>
                              <AdminDashboard />
                            </AdminOnly>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/app/admin/system"
                        element={
                          <ProtectedRoute>
                            <AdminOnly>
                              <SystemHealth />
                            </AdminOnly>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/app/admin/system/health"
                        element={
                          <ProtectedRoute>
                            <AdminOnly>
                              <SystemHealth />
                            </AdminOnly>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/app/admin/users"
                        element={
                          <ProtectedRoute>
                            <AdminOnly>
                              <UserManagement />
                            </AdminOnly>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/app/admin/analytics"
                        element={
                          <ProtectedRoute>
                            <AdminOnly>
                              <OrganizationAnalytics />
                            </AdminOnly>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/app/admin/audit"
                        element={
                          <ProtectedRoute>
                            <AdminOnly>
                              <AuditLogs />
                            </AdminOnly>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/app/admin/organizations"
                        element={
                          <ProtectedRoute>
                            <AdminOnly>
                              <OrganizationManagement />
                            </AdminOnly>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/app/admin/jobs"
                        element={
                          <ProtectedRoute>
                            <AdminOnly>
                              <BackgroundJobs />
                            </AdminOnly>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/app/admin/permissions"
                        element={
                          <ProtectedRoute>
                            <AdminOnly>
                              <UserManagement />
                            </AdminOnly>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/app/admin/database"
                        element={
                          <ProtectedRoute>
                            <AdminOnly>
                              <SystemHealth />
                            </AdminOnly>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/app/admin/imports"
                        element={
                          <ProtectedRoute>
                            <AdminOnly>
                              <BackgroundJobs />
                            </AdminOnly>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/app/admin/settings"
                        element={
                          <ProtectedRoute>
                            <AdminOnly>
                              <SystemHealth />
                            </AdminOnly>
                          </ProtectedRoute>
                        }
                      />

                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </>
                </TooltipProvider>
              </StripeProvider>
            </QueryClientProvider>
          </SessionProvider>
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
};

export default App;
